import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Spinner } from '@apps/web/components/ui/spinner';
import { Textarea } from '@apps/web/components/ui/textarea';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { AgentTraceViewTabs } from '@apps/web/routes/_app/admin/agent-traces/agent_trace_view_tabs';
import {
    useAgentPrompt,
    useAgentPrompts,
} from '@apps/web/swr_data_fetching/admin_agent_prompt_queries';
import { type AgentPrompt } from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/agent-traces/prompts')({
    head: () => ({
        meta: [
            {
                title: 'Agent Prompts | Phoebe Admin',
            },
        ],
    }),
    component: AgentPromptsPage,
});

function AgentPromptsPage() {
    const { data: prompts } = useAgentPrompts();
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const promptKey = selectedKey ?? prompts?.[0]?.prompt_key ?? null;
    const { data: prompt, mutate } = useAgentPrompt(promptKey);

    return (
        <div className="flex flex-col gap-6 p-6">
            <AgentTraceViewTabs activeView="prompts" />
            <div className="flex items-start justify-between gap-6">
                <div className="space-y-1">
                    <h1 className="text-lg font-semibold">Agent prompts</h1>
                    <p className="text-muted-foreground max-w-2xl text-sm">
                        Edit an agent’s system prompt without a deploy. Saving
                        applies to every organization on the agent’s next run.
                    </p>
                </div>
                {prompts ? (
                    <Select
                        value={promptKey ?? undefined}
                        onValueChange={setSelectedKey}
                    >
                        <SelectTrigger className="w-64" aria-label="Prompt">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {prompts.map((summary) => (
                                <SelectItem
                                    key={summary.prompt_key}
                                    value={summary.prompt_key}
                                >
                                    {summary.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                ) : null}
            </div>
            {prompt ? (
                <PromptEditor
                    // Remount after a save so the draft reloads.
                    key={`${prompt.prompt_key}:${prompt.updated_at ?? 'built-in'}`}
                    prompt={prompt}
                    onSaved={(updated) => mutate(updated, false)}
                />
            ) : (
                <Spinner />
            )}
        </div>
    );
}

function PromptEditor({
    prompt,
    onSaved,
}: {
    readonly prompt: AgentPrompt;
    readonly onSaved: (updated: AgentPrompt) => void;
}) {
    const [draft, setDraft] = useState(prompt.effective_body);
    const [isSaving, setIsSaving] = useState(false);
    const isDirty = draft !== prompt.effective_body;

    useEffect(() => {
        if (!isDirty) {
            return undefined;
        }
        function warnBeforeUnload(event: BeforeUnloadEvent) {
            event.preventDefault();
        }
        window.addEventListener('beforeunload', warnBeforeUnload);
        return () =>
            window.removeEventListener('beforeunload', warnBeforeUnload);
    }, [isDirty]);

    async function save(body: string | null) {
        setIsSaving(true);
        const pathParams = { prompt_key: prompt.prompt_key };
        try {
            const updated =
                body === null
                    ? await getApiClient().delete({
                          url: '/admin/agent-traces/prompts/{prompt_key}',
                          pathParams,
                      })
                    : await getApiClient().put({
                          url: '/admin/agent-traces/prompts/{prompt_key}',
                          pathParams,
                          body: { body },
                          reason: 'admin-agent-prompt-save',
                      });
            onSaved(updated);
            toast.success(
                body === null ? 'Restored the built-in prompt' : 'Prompt saved',
                { persistNotification: false },
            );
        } catch {
            toast.error('Failed to save prompt', {
                persistNotification: false,
            });
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between gap-4">
                <div className="space-y-1">
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-medium">{prompt.label}</h2>
                        {prompt.is_overridden ? (
                            <Badge variant="default">Edited</Badge>
                        ) : (
                            <Badge variant="outline">Built-in</Badge>
                        )}
                    </div>
                    <p className="text-muted-foreground text-sm">
                        {prompt.description}
                    </p>
                    {prompt.updated_at ? (
                        <p className="text-muted-foreground text-sm">
                            Edited by {prompt.updated_by_email ?? 'unknown'} on{' '}
                            {new Date(prompt.updated_at).toLocaleString()}
                        </p>
                    ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    {prompt.is_overridden ? (
                        <Button
                            variant="outline"
                            disabled={isSaving}
                            onClick={() => void save(null)}
                        >
                            Restore built-in
                        </Button>
                    ) : null}
                    <Button
                        variant="outline"
                        disabled={!isDirty || isSaving}
                        onClick={() => setDraft(prompt.effective_body)}
                    >
                        Discard changes
                    </Button>
                    <Button
                        disabled={!isDirty || isSaving || draft.trim() === ''}
                        onClick={() => void save(draft)}
                    >
                        {isSaving ? <Spinner size="xs" /> : null}
                        Save
                    </Button>
                </div>
            </div>
            <Textarea
                aria-label={`${prompt.label} prompt`}
                className="[field-sizing:fixed] h-[60vh] font-mono text-xs"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
            />
        </div>
    );
}
